import { useEffect, useRef } from 'react';
import { useTopAdBar } from 'App/components/TopAdBar/useTopAdBar';
import useTopBar from 'App/components/Topbar/useTopBar';
import useIsPrinting from 'commons/hooks/useIsPrinting';
import { useResponsive } from '../Responsive';

export interface UseIonPageStratusProps {
  hasHeaderTop: boolean;
  hasHeaderFixed: boolean;
}

/*
 * Hook to manage the behavior of the IonPageStratus component.
 * It handles the visibility of the top bar and the fixed header.
 */
function useIonPageStratus({
  hasHeaderTop,
  hasHeaderFixed,
}: Readonly<UseIonPageStratusProps>) {
  const { isMobile, isDesktop } = useResponsive();
  const {
    setHeaderNavigation,
    showTopbar,
    stopScrollListener,
    startScrollListener,
    isShowTopbar,
    height: heightTopBar,
  } = useTopBar();

  const { height: heightTopAdBar } = useTopAdBar();

  const { isPrinting } = useIsPrinting();

  const refFixedHeader = useRef<HTMLDivElement>(null);
  const refContent = useRef<HTMLDivElement>(null);
  const mustRenderFixedHeader = hasHeaderFixed && isDesktop && !isPrinting;

  // Effect to manage header navigation based on the presence or absence of headerTop.
  // Sets the header navigation to undefined if headerTop is not provided.
  // Cleans up by resetting the header navigation on component unmount or headerTop change.
  useEffect(() => {
    !hasHeaderTop && setHeaderNavigation(undefined);
    return () => setHeaderNavigation(undefined);
  }, [hasHeaderTop, setHeaderNavigation]);

  // Effect to ensure the top bar is always visible on component mount.
  // It calls showTopbar function to enforce the visibility of the top bar.
  // It also starts the scroll listener if the header is fixed
  useEffect(() => {
    showTopbar();
    if (mustRenderFixedHeader) startScrollListener();
    return stopScrollListener;
  }, [
    hasHeaderTop,
    mustRenderFixedHeader,
    showTopbar,
    startScrollListener,
    stopScrollListener,
  ]);

  // Effect to manage the width of the fixed header based on the content width.
  useEffect(() => {
    if (typeof ResizeObserver !== 'undefined' && mustRenderFixedHeader) {
      const resizeObserver = new ResizeObserver(([entry]) => {
        const currentWidth = entry.contentRect.width;
        if (refFixedHeader.current) {
          refFixedHeader.current.style.width = `${currentWidth}px`;
        }
      });
      if (refContent.current) {
        resizeObserver.observe(refContent.current);
      }
    }
  }, [mustRenderFixedHeader]);

  return {
    refFixedHeader,
    refContent,
    isMobile,
    isShowTopbar,
    height: heightTopBar + heightTopAdBar,
    mustRenderFixedHeader,
  };
}

export default useIonPageStratus;
